const React = require('react');
const { createElement: h, useEffect, useMemo, useState } = React;
require('./WorkflowTagModal.css');

var TAG_COLOR_OPTIONS = [
  { name: 'Slate', value: '#64748b' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Rose', value: '#f43f5e' },
  { name: 'Violet', value: '#8b5cf6' }
];

function normalizeTag(tag) {
  if (!tag) return null;

  if (typeof tag === 'string') {
    var name = String(tag || '').trim();
    if (!name) return null;
    return { name: name, color: TAG_COLOR_OPTIONS[0].value };
  }

  if (typeof tag === 'object') {
    var tagName = String(tag.name !== undefined && tag.name !== null ? tag.name : tag.label || '').trim();
    if (!tagName) return null;
    return {
      name: tagName,
      color: String(tag.color || tag.hex || TAG_COLOR_OPTIONS[0].value).trim() || TAG_COLOR_OPTIONS[0].value
    };
  }

  return null;
}

function normalizeTags(tags) {
  var seen = {};
  return (Array.isArray(tags) ? tags : [])
    .map(normalizeTag)
    .filter(Boolean)
    .filter(function(tag) {
      var key = tag.name.toLowerCase();
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    });
}

function tagKey(tag) {
  return tag && tag.name ? String(tag.name).trim().toLowerCase() : '';
}

function WorkflowTagModal(props) {
  var workflowName = props && props.workflowName ? String(props.workflowName) : 'Untitled workflow';
  var initialTags = Array.isArray(props && props.tags) ? props.tags : [];
  var suggestedTags = Array.isArray(props && props.suggestedTags) ? props.suggestedTags : [];
  var onSave = props && props.onSave ? props.onSave : function() {};
  var onClose = props && props.onClose ? props.onClose : function() {};
  var saving = !!(props && props.saving);

  var [tags, setTags] = useState(function() {
    return normalizeTags(initialTags);
  });
  var [draftName, setDraftName] = useState('');
  var [draftColor, setDraftColor] = useState(TAG_COLOR_OPTIONS[0].value);
  var [error, setError] = useState('');

  useEffect(function() {
    setTags(normalizeTags(initialTags));
    setDraftName('');
    setDraftColor(TAG_COLOR_OPTIONS[0].value);
    setError('');
  }, [workflowName, JSON.stringify(initialTags)]);

  var palette = useMemo(function() {
    return TAG_COLOR_OPTIONS.slice();
  }, []);

  var normalizedSuggestedTags = useMemo(function() {
    return normalizeTags(suggestedTags).filter(function(tag) {
      return !tags.some(function(existingTag) {
        return tagKey(existingTag) === tagKey(tag);
      });
    });
  }, [suggestedTags, tags]);

  function handleAddTag() {
    var name = String(draftName || '').trim();
    if (!name) {
      setError('Enter a tag name.');
      return;
    }

    var lower = name.toLowerCase();
    if (tags.some(function(tag) { return tag.name.toLowerCase() === lower; })) {
      setError('That tag already exists for this workflow.');
      return;
    }

    setTags(function(prev) {
      return normalizeTags(prev.concat([{ name: name, color: draftColor || TAG_COLOR_OPTIONS[0].value }]));
    });
    setDraftName('');
    setDraftColor(TAG_COLOR_OPTIONS[0].value);
    setError('');
  }

  function handleAddSuggestedTag(tag) {
    var nextTag = normalizeTag(tag);
    if (!nextTag) return;

    if (tags.some(function(existingTag) {
      return tagKey(existingTag) === tagKey(nextTag);
    })) {
      return;
    }

    setTags(function(prev) {
      return normalizeTags(prev.concat([nextTag]));
    });
    setError('');
  }

  function handleRemoveTag(tagName) {
    setTags(function(prev) {
      return prev.filter(function(tag) {
        return tag.name.toLowerCase() !== String(tagName || '').toLowerCase();
      });
    });
  }

  function handleSubmit(event) {
    event.preventDefault();
    onSave(tags);
  }

  return h('div', { className: 'wtm-backdrop', role: 'presentation', onClick: onClose },
    h('div', {
      className: 'wtm-modal',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-label': 'Edit workflow tags',
      onClick: function(event) { event.stopPropagation(); }
    },
      h('div', { className: 'wtm-header' },
        h('div', null,
          h('p', { className: 'wtm-eyebrow' }, 'Tags'),
          h('h3', {
            className: 'wtm-title',
            title: workflowName
          }, workflowName)
        ),
        h('button', {
          className: 'wtm-close',
          type: 'button',
          onClick: onClose,
          'aria-label': 'Close'
        }, 'x')
      ),
      h('form', { className: 'wtm-form', onSubmit: handleSubmit },
        h('div', { className: 'wtm-existing' },
          tags.length
            ? tags.map(function(tag) {
                return h('span', {
                  key: tag.name,
                  className: 'wtm-tag',
                  style: { '--tag-color': tag.color }
                },
                  h('span', { className: 'wtm-tag__dot' }),
                  h('span', { className: 'wtm-tag__name' }, tag.name),
                  h('button', {
                    type: 'button',
                    className: 'wtm-tag__remove',
                    onClick: function() { handleRemoveTag(tag.name); },
                    'aria-label': 'Remove ' + tag.name
                  }, 'x')
                );
              })
            : h('p', { className: 'wtm-empty' }, 'No tags added yet.')
        ),
        normalizedSuggestedTags.length
          ? h('div', { className: 'wtm-suggestions' },
              h('p', { className: 'wtm-suggestions__label' }, 'Existing tags'),
              h('div', { className: 'wtm-suggestions__list' },
                normalizedSuggestedTags.map(function(tag) {
                  return h('button', {
                    key: tag.name,
                    type: 'button',
                    className: 'wtm-suggestion',
                    style: { '--tag-color': tag.color },
                    onClick: function() { handleAddSuggestedTag(tag); }
                  },
                    h('span', { className: 'wtm-suggestion__dot' }),
                    h('span', { className: 'wtm-suggestion__name' }, tag.name)
                  );
                })
              )
            )
          : null,
        h('p', { className: 'wtm-help' }, 'Add an existing tag or create a new one below.'),
        h('div', { className: 'wtm-row' },
          h('label', { className: 'wtm-field' },
            h('span', { className: 'wtm-label' }, 'Tag name'),
            h('input', {
              className: 'wtm-input',
              type: 'text',
              value: draftName,
              onChange: function(event) { setDraftName(event.target.value); },
              onKeyDown: function(event) {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleAddTag();
                }
              },
              placeholder: 'E.g. Finance'
            })
          ),
          h('label', { className: 'wtm-field wtm-field--color' },
            h('span', { className: 'wtm-label' }, 'Color'),
            h('select', {
              className: 'wtm-select',
              value: draftColor,
              onChange: function(event) { setDraftColor(event.target.value); }
            },
              palette.map(function(option) {
                return h('option', { key: option.value, value: option.value }, option.name);
              })
            )
          )
        ),
        h('div', { className: 'wtm-inline-actions' },
          h('button', { className: 'wtm-add', type: 'button', onClick: handleAddTag }, '+ Add tag')
        ),
        h('div', { className: 'wtm-swatches', 'aria-label': 'Tag colors' },
          palette.map(function(option) {
            var isSelected = option.value === draftColor;
            return h('button', {
              key: option.value,
              type: 'button',
              className: 'wtm-swatch' + (isSelected ? ' wtm-swatch--active' : ''),
              style: { '--tag-color': option.value },
              onClick: function() { setDraftColor(option.value); }
            },
              h('span', { className: 'wtm-swatch__dot' }),
              h('span', { className: 'wtm-swatch__name' }, option.name)
            );
          })
        ),
        error ? h('p', { className: 'wtm-error' }, error) : null,
        h('div', { className: 'wtm-actions' },
          h('button', { className: 'wtm-secondary', type: 'button', onClick: onClose }, 'Cancel'),
          h('button', {
            className: 'wtm-save',
            type: 'submit',
            disabled: saving
          }, saving ? 'Saving...' : 'Done')
        )
      )
    )
  );
}

module.exports = WorkflowTagModal;
